import {useEffect, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {sendVerificationCode, verifyCode} from '../../api/authApi';
import {defaultAlert} from "../../utils/alert";

const FindPasswordPage = () => {
    const navigate = useNavigate();
    const [memberId, setMemberId] = useState('');
    const [phone, setPhone] = useState('');
    const [verificationCode, setVerificationCode] = useState('');
    const [isCodeSent, setIsCodeSent] = useState(false);
    const [isCodeVerified, setIsCodeVerified] = useState(false);
    const [timeLeft, setTimeLeft] = useState(180); // 3분

    // 타이머
    useEffect(() => {
        if (isCodeSent && timeLeft > 0) {
            const timer = setInterval(() => {
                setTimeLeft(prev => prev - 1);
            }, 1000);
            return () => clearInterval(timer);
        } else if (isCodeSent && timeLeft === 0) {
            defaultAlert('인증번호 입력시간이 초과되었습니다. 다시 확인하여 주시기 바랍니다.');
            setIsCodeSent(false);
            setVerificationCode('');
        }
    }, [isCodeSent, timeLeft]);

    // 인증번호 발송
    const handleSendCode = async () => {
        if (!memberId || !phone) {
            defaultAlert('회원ID와 휴대폰 번호를 입력해주세요.');
            return;
        }

        try {
            // 비밀번호 찾기용: memberName 자리에 memberId 전달
            await sendVerificationCode({memberName: memberId, phone});
            setIsCodeSent(true);
            setTimeLeft(180);
            defaultAlert('인증번호가 발송되었습니다.');
        } catch (error) {
            defaultAlert('인증번호 발송에 실패했습니다.');
            console.error(error);
        }
    };

    // 인증번호 확인
    const handleVerifyCode = async () => {
        if (!verificationCode) {
            defaultAlert('인증번호를 입력해주세요.');
            return;
        }

        try {
            const res = await verifyCode({phone, code: verificationCode});
            if (res.data.data) {
                setIsCodeVerified(true);
                defaultAlert('인증이 완료되었습니다.');
                navigate('/reset-password', {
                    state: {memberId, phone, verified: true}
                });
            } else {
                defaultAlert('인증번호가 일치하지 않습니다.');
            }
        } catch (error) {
            defaultAlert('입력한 정보가 맞지 않습니다. 다시 확인하여 주시기 바랍니다.');
            console.error(error);
        }
    };

    const formatTime = (seconds: number) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    };

    return (
        <div className="page-wrapper">
            <header className="sub-header">
                <div className="header-inner">
                    <button type="button" className="backBtn" onClick={() => navigate(-1)}>
                        <span className="blind">뒤로가기</span>
                    </button>
                </div>
            </header>

            <div className="bottom-btn">
                <button type="button" className="btn cancel" onClick={() => navigate(-1)}>
                    이전
                </button>
                <button
                    type="button"
                    className="btn confirm"
                    onClick={() => {
                        if (isCodeVerified) {
                            navigate('/reset-password', {
                                state: {memberId, phone, verified: true}
                            });
                        }
                    }}
                    disabled={!isCodeVerified}
                >
                    확인
                </button>
            </div>

            <main className="container">
                <section className="tit-box-wrap">
                    <h2 className="headline">비밀번호 찾기</h2>
                    <p className="subtit">가입하신 정보를 입력해주세요</p>
                </section>

                <section>
                    <div className="inner-box">
                        <form>
                            <fieldset>
                                <legend>비밀번호 찾기 폼</legend>
                                <div className="form-box">
                                    <div className="form-item">
                                        <label className="input-tit">회원ID</label>
                                        <div className="input-box">
                                            <input
                                                type="text"
                                                placeholder="회원ID를 입력해주세요"
                                                value={memberId}
                                                onChange={(e) => setMemberId(e.target.value)}
                                                disabled={isCodeSent}
                                            />
                                        </div>
                                    </div>

                                    <div className="form-item">
                                        <label className="input-tit">휴대폰 번호</label>
                                        <div className="input-set">
                                            <div className="input-box">
                                                <input
                                                    type="tel"
                                                    placeholder="휴대폰 번호를 입력해주세요"
                                                    value={phone}
                                                    onChange={(e) => setPhone(e.target.value)}
                                                    disabled={isCodeSent}
                                                />
                                            </div>
                                            <button
                                                type="button"
                                                className="check-btn"
                                                onClick={handleSendCode}
                                                disabled={isCodeSent || !memberId || !phone}
                                            >
                                                {isCodeSent ? '발송완료' : '인증하기'}
                                            </button>
                                        </div>
                                    </div>

                                    {isCodeSent && (
                                        <div className="form-item">
                                            <label className="input-tit">인증번호</label>
                                            <div className="input-set">
                                                <div className="input-box">
                                                    <input
                                                        type="text"
                                                        placeholder="인증번호를 입력해주세요"
                                                        value={verificationCode}
                                                        onChange={(e) => setVerificationCode(e.target.value)}
                                                        disabled={isCodeVerified}
                                                    />
                                                    <span className="time"
                                                          style={{color: timeLeft < 60 ? 'red' : 'inherit'}}>
                                                        {formatTime(timeLeft)}
                                                    </span>
                                                </div>
                                                <button
                                                    type="button"
                                                    className="check-btn"
                                                    onClick={handleVerifyCode}
                                                    disabled={isCodeVerified || timeLeft === 0}
                                                >
                                                    {isCodeVerified ? '완료' : '확인'}
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </fieldset>
                        </form>
                    </div>
                </section>

                <section>
                    <div className="inner">
                        <p className="txt-guide">
                            아이디를 잊으셨나요? <a href="/find-id">아이디 찾기</a>
                        </p>
                    </div>
                </section>
            </main>
        </div>
    );
};

export default FindPasswordPage;